import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileText } from "lucide-react";
import { COMMODITY_DATA, ORIGIN_LOTS, findOrigin, slugify } from "../mock";
import RegionMap from "../components/trading/RegionMap";
import HarvestCalendar from "../components/trading/HarvestCalendar";
import { useCupping } from "../hooks/useCupping";

export default function OriginPage() {
  const { commodity, slug } = useParams();
  const data = COMMODITY_DATA[commodity];
  const origin = data && findOrigin(commodity, slug);
  const cupping = useCupping();
  if (!origin) return <Navigate to="/trading/coffee" replace />;
  const lots = ORIGIN_LOTS(commodity, origin.region);
  const others = data.origins.items.filter((o) => o.region !== origin.region);

  return (
    <main className="bg-[#f6f1e7]" data-testid={`origin-page-${slug}`}>
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden bg-[#10271a] grain">
        <img src={origin.image} alt={origin.region} className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#10271a] via-[#10271a]/40 to-[#10271a]/30" />
        <div className="relative h-full mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col justify-end pb-14 text-[#f6f1e7]">
          <Link to={`/trading/${commodity}`} className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-[#f6f1e7]/80 hover:text-[#f6f1e7] mb-8" data-testid="origin-back"><ArrowLeft size={14} /> {data.label} journey</Link>
          <p className="text-[#b08a5f] text-xs tracking-[0.25em] uppercase">{origin.no} · {origin.country} · {origin.towns}</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.0]" data-testid="region-name">{origin.region}</h1>
          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
            <Fact label="Altitude" value={origin.altitude} /><Fact label="Varietal / species" value={origin.varietal} /><Fact label="Process" value={origin.process} />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-start">
          <div>
            <p className="text-[#7c5230] text-xs tracking-[0.25em] uppercase mb-5">The region</p>
            {origin.story.map((p, i) => <p key={i} className="text-[#10271a] text-lg leading-relaxed mb-5">{p}</p>)}
            <p className="text-[#7c5230] text-xs tracking-[0.25em] uppercase mt-12 mb-5">Harvest calendar</p>
            <HarvestCalendar commodity={commodity} harvest={origin.harvest} />
          </div>
          <RegionMap commodity={commodity} origins={data.origins.items} active={origin} />
        </div>
      </section>

      <section className="bg-[#ece2cf] py-20 md:py-28" data-testid="origin-lots">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="text-[#7c5230] text-xs tracking-[0.25em] uppercase mb-5">Lots from this region</p>
          <h2 className="font-display text-3xl md:text-5xl font-light text-[#10271a]">What we're <em className="italic text-[#1f4a33]">shipping.</em></h2>
          {lots.length === 0 ? (
            <p className="mt-8 text-[#5e6b61]">No current lots from this region — <Link to="/wholesale" className="underline">ask the trade desk</Link> about forward availability.</p>
          ) : (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lots.map((lot) => {
                const c = cupping[lot.id];
                return (
                  <article key={lot.id} className="bg-[#faf6ec] rounded-3xl overflow-hidden border border-[#10271a]/5" data-testid={`origin-lot-${lot.id}`}>
                    <div className="relative aspect-[16/9]"><img src={lot.image} alt={lot.name} className="h-full w-full object-cover" />
                      {c && <span data-testid={`cupping-badge-${lot.id}`} className="absolute top-4 right-4 bg-[#10271a]/85 backdrop-blur text-[#f6f1e7] rounded-full px-3 py-1.5 text-xs"><span className="font-display text-lg">{c.score}</span> <span className="text-[#f6f1e7]/60">/ {c.scale.split("/")[1]?.trim()}</span></span>}
                    </div>
                    <div className="p-6">
                      <p className="text-[#7c5230] text-[11px] tracking-[0.18em] uppercase">{lot.tag}</p>
                      <h3 className="font-display text-2xl text-[#10271a] mt-1">{lot.name}</h3>
                      <p className="mt-3 text-sm text-[#5e6b61] leading-relaxed">{c?.notes || lot.story}</p>
                      <div className="mt-5 flex items-center gap-5 text-[12px] tracking-[0.1em] uppercase font-semibold">
                        <Link to={`/spec/${commodity}/${lot.id}`} className="inline-flex items-center gap-2 text-[#1f4a33]"><FileText size={14} /> Spec sheet</Link>
                        <Link to={`/wholesale?commodity=${commodity}`} className="inline-flex items-center gap-2 text-[#5e6b61] hover:text-[#10271a]">Enquire <ArrowRight size={14} /></Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 border-t border-[#10271a]/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <p className="text-[#7c5230] text-xs tracking-[0.25em] uppercase">Other {data.label.toLowerCase()} regions</p>
          <div className="flex flex-wrap gap-2">
            {others.map((o) => <Link key={o.region} to={`/origins/${commodity}/${slugify(o.region)}`} data-testid={`origin-link-${slugify(o.region)}`} className="px-4 py-2 rounded-full border border-[#10271a]/15 text-[12px] tracking-[0.1em] uppercase font-semibold text-[#10271a]/75 hover:border-[#1f4a33] hover:text-[#1f4a33]">{o.region}</Link>)}
          </div>
        </div>
      </section>
    </main>
  );
}

function Fact({ label, value }) {
  return <div><p className="text-[10px] tracking-[0.2em] uppercase text-[#f6f1e7]/60">{label}</p><p className="mt-1 text-[#f6f1e7] font-medium">{value}</p></div>;
}
